/**
 * Direct Messages Query Hooks
 *
 * Polling model (no WebSocket by design): every mounted query polls only
 * while the tab is visible. Open threads at 12s (user is watching), thread
 * lists at 30s (feeds inboxes AND the sidebar unread badges — without list
 * polling an idle inbox never learns about incoming messages, because the
 * global query client has refetchOnWindowFocus: false).
 *
 * All hooks carry the TEMP canSeePaidDms tester gate (FRONT-307 rollout) on
 * top of the auth gate — remove it from `enabled` at full release.
 */

import { useQuery } from '@tanstack/react-query';
import { useDocumentVisible } from '@/hooks/use-document-visible';
import { canSeePaidDms } from '@/shared/lib/auth/permissions';
import { useAuthStore } from '@/shared/stores/auth-store';
import { dmClient } from '../client/dm.client';
import { queryKeys } from '../query-keys';

/**
 * Open-thread cadence — tighter than notifications' 60s because the user is
 * watching the conversation; still far below the BE's 30/min send throttle.
 */
const THREAD_POLL_MS = 12_000;

/** Inbox/badge cadence — between the open-thread 12s and notifications' 60s. */
const LIST_POLL_MS = 30_000;

function usePaidDmsEnabled(): boolean {
  // Auth-gated only — subscriber DM endpoints are deliberately not subscription-gated.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  return isAuthenticated && canSeePaidDms(user);
}

export function useDmThreads(options?: { enabled?: boolean }) {
  const enabled = usePaidDmsEnabled();
  const visible = useDocumentVisible();

  return useQuery({
    queryKey: queryKeys.dm.threads(),
    queryFn: () => dmClient.listThreads(),
    staleTime: 1000 * 30,
    refetchInterval: visible ? LIST_POLL_MS : false,
    enabled: enabled && (options?.enabled ?? true),
  });
}

export function useStudioDmThreads(options?: { enabled?: boolean }) {
  const gateEnabled = usePaidDmsEnabled();
  const visible = useDocumentVisible();

  return useQuery({
    queryKey: queryKeys.dm.studioThreads(),
    queryFn: () => dmClient.listStudioThreads(),
    staleTime: 1000 * 30,
    refetchInterval: visible ? LIST_POLL_MS : false,
    enabled: gateEnabled && (options?.enabled ?? true),
  });
}

/**
 * Thread history (oldest-first, both perspectives). `threadUuid` is undefined
 * before the first send creates the thread — the query stays disabled.
 */
export function useDmMessages(threadUuid: string | undefined, options?: { poll?: boolean }) {
  const enabled = usePaidDmsEnabled();
  const visible = useDocumentVisible();
  const poll = (options?.poll ?? true) && visible;

  return useQuery({
    queryKey: queryKeys.dm.messages(threadUuid ?? 'pending'),
    queryFn: () => dmClient.getMessages(threadUuid as string),
    staleTime: 1000 * 10,
    refetchInterval: poll ? THREAD_POLL_MS : false,
    enabled: enabled && Boolean(threadUuid),
  });
}
